import React, { memo, useCallback, useMemo } from 'react'
import tw from 'twin.macro'
import { Form, Formik } from 'formik'
import { useSnackbar } from 'notistack'
import { useSelector } from 'react-redux'
import { Button } from 'src/components/shared/Button'
import { SectionCard } from 'src/components/shared/SectionCard'
import { FormikSwitch } from 'src/components/shared/form/formik/FormikSwitch'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import { AdminUpdateRetryConfig } from 'src/constants/actionTypes'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import type { RetryConfig } from 'src/interfaces/retryConfig'
import { getRetryConfigSaving } from 'src/selectors/retryConfig'
import { editActionConfigValidator } from './EditActionConfigForm.validator'

const FORM_ID = 'edit-action-config-form'

interface Props {
  config: RetryConfig | null
  configId: string
}

interface FormValues {
  maxAttempts: number | ''
  backoffMultiplier: number | ''
  timeoutMinutes: number | ''
  currentAttempt: number | ''
  disabled: boolean
}

const buildInitialValues = (config: RetryConfig | null): FormValues => {
  return {
    maxAttempts: config?.maxAttempts ?? '',
    backoffMultiplier: config?.backoffMultiplier ?? '',
    timeoutMinutes: config?.timeoutMinutes ?? '',
    currentAttempt: config?.currentAttempt ?? '',
    disabled: config?.disabled ?? false,
  }
}

/**
 * `enableReinitialize` re-seeds the form after the update saga's post-save refetch replaces
 * `config` with the server's own values — the same round trip the detail page's other cards
 * already depend on.
 *
 * The Save button lives in `SectionCard`'s `actions` slot, outside `<Form>`, and is wired back to
 * it via `form={FORM_ID}` — the same split `UpdateRetryTemplateModal` uses for its footer button.
 */
const EditActionConfigFormComponent = ({ config, configId }: Props) => {
  const isSaving = useSelector(getRetryConfigSaving)
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const snackbar = useSnackbar()

  const initialValues = useMemo(() => {
    return buildInitialValues(config)
  }, [config])

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      const result = await asyncDispatchWithErrorToast(
        AdminUpdateRetryConfig.request({
          configId,
          maxAttempts: Number(values.maxAttempts),
          backoffMultiplier: Number(values.backoffMultiplier),
          timeoutMinutes: Number(values.timeoutMinutes),
          currentAttempt: Number(values.currentAttempt),
          disabled: values.disabled,
        }),
      )

      if (!result) {
        return
      }

      snackbar?.enqueueSnackbar('Action config updated', { variant: 'success' })
    },
    [asyncDispatchWithErrorToast, configId, snackbar],
  )

  // Render nothing rather than an empty shell while the detail is still loading or missing.
  if (!config) {
    return null
  }

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      enableReinitialize
      validationSchema={editActionConfigValidator}
      onSubmit={handleSubmit}
    >
      <SectionCard
        title="Edit action config"
        dataTestId="edit-action-config-form-card"
        actions={
          <Button
            size="small"
            color="dark"
            type="submit"
            form={FORM_ID}
            loading={isSaving}
            disabled={isSaving}
            data-testid="edit-action-config-save-button"
          >
            Save
          </Button>
        }
      >
        <Form id={FORM_ID} data-testid="edit-action-config-form">
          <div css={tw`flex flex-col gap-4`}>
            <FormikTextField<FormValues>
              name="maxAttempts"
              label="Max attempts"
              type="number"
              data-testid="edit-action-config-max-attempts-input"
            />
            <FormikTextField<FormValues>
              name="backoffMultiplier"
              label="Backoff multiplier"
              type="number"
              data-testid="edit-action-config-backoff-multiplier-input"
            />
            <FormikTextField<FormValues>
              name="timeoutMinutes"
              label="Timeout (minutes)"
              type="number"
              data-testid="edit-action-config-timeout-minutes-input"
            />
            <FormikTextField<FormValues>
              name="currentAttempt"
              label="Current attempts"
              type="number"
              data-testid="edit-action-config-current-attempt-input"
            />
            <FormikSwitch<FormValues> name="disabled" label="Disabled" />
          </div>
        </Form>
      </SectionCard>
    </Formik>
  )
}

export const EditActionConfigForm = memo(EditActionConfigFormComponent)
